<template>
    <div class="chart-container">
      <canvas ref="lineChart"></canvas>
      <div v-if="loading" class="loading">데이터 로딩 중...</div>
      <div v-if="error" class="error">{{ error }}</div>
      <div v-if="!loading && !error && (!chartData || chartData.length === 0)" class="no-data">
        데이터가 없습니다
      </div>
    </div>
  </template>
  
  <script>
  import { Chart } from 'chart.js/auto';
  import axios from 'axios';
  import 'chartjs-adapter-date-fns';
  
  export default {
    name: 'LineChartComponent',
    data() {
      return {
        chart: null,
        loading: false,
        error: null,
        chartData: [],
        baseUrl: process.env.VUE_APP_API_URL || '' // 환경변수에서 API URL 가져오기
      }
    },
    async mounted() {
      await this.fetchData();
      // 1분마다 데이터 갱신
      this.intervalId = setInterval(this.fetchData, 60000);
    },
    beforeUnmount() { // beforeDestroy를 beforeUnmount로 변경
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      if (this.chart) {
        this.chart.destroy();
      }
    },
    methods: {
      async fetchData() {
        try {
          this.loading = true;
          this.error = null;
          
          const response = await axios.get(`${this.baseUrl}/api/temperature-data`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          const data = response.data;
          console.log('Fetched data:', data);

          if (!data || data.length === 0) {
            this.error = '데이터가 없습니다';
            return;
          }

          this.chartData = data;
          this.renderChart();
        } catch (err) {
          console.error('Error details:', err);
          this.error = `데이터 로딩 실패: ${err.response?.data?.message || err.message}`;
        } finally {
          this.loading = false;
        }
      },
      renderChart() {
        if (this.chart) {
          this.chart.destroy();
        }
  
        const ctx = this.$refs.lineChart.getContext('2d');
        const deviceGroups = {};
        
        this.chartData.forEach(item => {
          const deviceId = item.devicename || item.tag_mac; // devicename이 없으면 tag_mac 사용
          if (!deviceGroups[deviceId]) {
            deviceGroups[deviceId] = {
              timestamps: [],
              temperatures: []
            };
          }
          deviceGroups[deviceId].timestamps.push(new Date(item.time_interval));
          deviceGroups[deviceId].temperatures.push(item.avg_temperature);
        });
  
        const datasets = Object.entries(deviceGroups).map(([deviceId, data], index) => ({
          label: deviceId,
          data: data.temperatures,
          borderColor: this.getColor(index),
          tension: 0.3,
          fill: false,
          pointRadius: 0,
          borderWidth: 2,
          showLine: true
        }));
  
        this.chart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: Object.values(deviceGroups)[0]?.timestamps || [],
            datasets: datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
                labels: {
                  usePointStyle: true,
                  padding: 20,
                  font: {
                    size: 12
                  }
                }
              },
              tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false
              },
              datalabels: {
                display: false
              }
            },
            scales: {
              x: {
                type: 'time',
                time: {
                  unit: 'hour',
                  displayFormats: {
                    hour: 'HH:mm'
                  }
                },
                grid: {
                  display: true,
                  color: '#f0f0f0'
                },
                title: {
                  display: true,
                  text: '시간',
                  font: {
                    size: 14
                  }
                },
                ticks: {
                  maxRotation: 0
                }
              },
              y: {
                grid: {
                  display: true,
                  color: '#f0f0f0'
                },
                title: {
                  display: true,
                  text: '온도 (°C)',
                  font: {
                    size: 14
                  }
                },
                ticks: {
                  callback: function(value) {
                    return value + '°C';
                  }
                }
              }
            },
            interaction: {
              intersect: false,
              mode: 'index'
            },
            elements: {
              point: {
                radius: 0,
                hoverRadius: 0
              },
              line: {
                tension: 0.3
              }
            }
          }
        });
      },
      getColor(index) {
        const colors = [
          '#4a90e2', // 파랑
          '#50e3c2', // 민트
          '#9013fe', // 보라
          '#008000', // 초록
          '#ca1305', // 빨강
          '#ffa500', // 주황
          '#ff69b4', // 핑크
          '#4b0082', // 남색
          '#800000', // 갈색
          '#006400'  // 진한 초록
        ];
        return colors[index % colors.length];
      }
    }
  };
  </script>
  
  <style scoped>
  .chart-container {
    position: relative;
    height: 400px;
    width: 100%;
  }
  
  .loading, .error, .no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
  }
  
  .error {
    color: #ca1305;
  }
  
  .no-data {
    color: #666;
  }
  </style>